import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { LoginRequest, LoginResponse } from '@/domain/interfaces/IAuthService'
import { AuthService } from '@/features/auth/services/auth.service'
import { LocalStorageService } from '@/infrastructure/storage/local-storage.service'
import { AI_PANEL_STORAGE_KEY } from '@/config/ai.config'
// Cycle-safe: useNotification → notification.store → (pinia/vue/util only); nothing leads back to
// auth.store or BaseService, so a static import is fine here (the error interceptor imports it the
// same way).
import { useNotification } from '@/composables/useNotification'
// NOTE: resetDatapoints is imported lazily inside logout() to avoid a circular
// import cycle. Static chain would be:
//   auth.store → useDatapoints → DatapointService extends BaseService
//   → http-client → error.interceptor → auth.store (cycle)
// Dynamic import defers the resolution until logout() is actually called.

export const useAuthStore = defineStore('auth', () => {
  const authService = new AuthService()

  const isAuthenticated = ref(false)
  const sessionChecked = ref(false)
  const loading = ref(false)
  const error = ref<string | null>(null)

  async function login(request: LoginRequest): Promise<LoginResponse> {
    loading.value = true
    error.value = null
    try {
      const response = await authService.login(request)
      if (response.success) {
        isAuthenticated.value = true
        sessionChecked.value = true
      } else {
        error.value = response.status?.message ?? 'Login failed'
      }
      return response
    } catch (e) {
      error.value = (e as Error).message
      throw e
    } finally {
      loading.value = false
    }
  }

  // Clears everything tied to the signed-in user before the page leaves for logout or login:
  // on a shared browser the next person to sign in must not inherit any of it.
  async function clearUserState(): Promise<void> {
    $reset()
    // Lazy imports — see note at top of file re: circular import avoidance.
    // User-store reset is FIRST and in its own try so a datapoints-import failure
    // can't leave the persisted (stale) profile behind — the whole reason this exists.
    try {
      const { useUserStore } = await import('@/features/user/stores/user.store')
      useUserStore().$reset()
    } catch {
      // Non-critical — full page reload re-hydrates from a now-reset localStorage.
    }
    try {
      const { resetDatapoints } = await import('@/composables/useDatapoints')
      resetDatapoints()
    } catch {
      // Non-critical — the caller is about to fully reload the page,
      // which re-initializes all module-level singletons anyway.
    }
    // Cached results-table layouts (persisted): the next person must get their own.
    try {
      const { useTableColumnsStore } = await import('@/stores/table-columns.store')
      useTableColumnsStore().$reset()
    } catch {
      // Non-critical — the server copy is the source of truth and replaces the cache on load.
    }
    // DEV-7125 — the AI panel store persists a screen→session map; it must not
    // leak to the next user on a shared browser. Guarded: jsdom test envs have
    // no localStorage, and a storage failure must never block logout.
    try {
      new LocalStorageService().remove(AI_PANEL_STORAGE_KEY)
    } catch {
      /* storage unavailable — never block logout */
    }
  }

  async function logout(): Promise<void> {
    await clearUserState()
    // Full page redirect — backend LogOff triggers OIDC end-session redirect
    window.location.href = '/Account/LogOut'
  }

  // For a user whose session has already ended. /Account/Login is anonymous: it sends them to
  // sign in and then back to the page they were on (the full path + query, parity with
  // auth.guard `to.fullPath` and Vue 2 session-manager).
  // Guards the whole redirect: several requests can fail the moment a session ends, and without
  // this each would fire its own toast and race the others' navigation, cutting the notice short.
  let redirecting = false

  async function redirectToLogin(): Promise<void> {
    if (redirecting) return
    redirecting = true
    await clearUserState()
    // Tell the user WHY they're being sent to sign in — otherwise an expired session is a silent
    // bounce to the login page. Pause so the toast is readable before the full-page navigation
    // (which would otherwise discard it immediately). Best-effort: a failed notification must
    // never block the redirect.
    try {
      useNotification().warning('Your session has expired. Please sign in again.')
      await new Promise((resolve) => setTimeout(resolve, 3000))
    } catch {
      /* notification is best-effort */
    }
    const returnUrl = window.location.pathname + window.location.search
    window.location.href = `/Account/Login?returnUrl=${encodeURIComponent(returnUrl)}`
  }

  // The session can't continue (RenewSession said no, or the idle timeout ran out). Log out
  // only if this browser still holds a session, i.e. the user signed in elsewhere or is idle.
  // If it has already ended (expired, cookies cleared), logout would be wrong: /Account/LogOut
  // is [Authorize], so the user would sign in, land back on the logout URL and be signed
  // straight out again. Unknown (offline) keeps the old behaviour and logs out.
  async function leaveSession(): Promise<void> {
    if ((await authService.hasSignedInSession()) === false) {
      await redirectToLogin()
    } else {
      await logout()
    }
  }

  async function renewSession(): Promise<boolean> {
    try {
      const valid = await authService.renewSession()
      if (valid) {
        isAuthenticated.value = true
      } else {
        $reset()
      }
      return valid
    } catch {
      $reset()
      return false
    }
  }

  async function checkSession(): Promise<boolean> {
    const valid = await authService.checkSession()
    isAuthenticated.value = valid
    sessionChecked.value = true
    return valid
  }

  function $reset() {
    isAuthenticated.value = false
    sessionChecked.value = false
    loading.value = false
    error.value = null
  }

  return {
    isAuthenticated,
    sessionChecked,
    loading,
    error,
    login,
    logout,
    redirectToLogin,
    leaveSession,
    renewSession,
    checkSession,
    $reset,
  }
}, {
  persist: {
    pick: ['isAuthenticated'],
  },
})
